import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DirectJobsScreeningResult } from '../../types/economicContribution';
import { resolveDevelopmentPathwayPhase } from '../reconcileDevelopmentPathway';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';

const MARKER = '\n\nModeled phase staffing\n';

function staffingBand(result: DirectJobsScreeningResult): string | null {
  const range = result.range;
  if (result.status !== 'calculated' || result.evidenceBasis !== 'modelled' || !range
    || !Number.isFinite(range.low) || !Number.isFinite(range.high)
    || range.low < 0 || range.high < range.low) return null;
  return `${range.low.toLocaleString('en-US')}–${range.high.toLocaleString('en-US')} direct onsite FTE`;
}

/** Generation-time projection of frozen staffing cases into the existing phase
 * plan, shared by reader, immutable document and office exports. Never computes
 * staffing, applies a whole-campus total to a phase, or rewrites saved reports. */
export function applyPhaseStaffingDisclosure(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  intent: ProjectIntent | null | undefined = report.projectIntentAtGen,
  reportType = report.reportType ?? '',
): SiteRagReport {
  if (reportType !== 'feasibility' || (!Array.isArray(report.developmentPathway) || !report.developmentPathway.length)) return report;
  const data = sed?.economicEvidence?.data;
  const screen = data?.screening;
  const usable = !!sed && !!screen && !!report.siteId && screen.siteId === report.siteId
    && (data?.screeningSources?.length ?? 0) > 0;
  const references = [...(report.references ?? [])];
  let citedSources: string | undefined;
  const citeSources = (): string => {
    if (citedSources !== undefined) return citedSources;
    citedSources = (data?.screeningSources ?? []).map(source => {
      const field = `economicEvidence.screeningSources.${source.id}`;
      let index = references.findIndex(ref => ref.field === field && ref.url === source.url
        && ref.qualification === 'estimated' && ref.sourceGrade === 'inference');
      if (index < 0) index = references.push({ field, source: `${source.publisher}: ${source.title} (${source.locator})`,
        url: source.url, retrievedAt: source.accessedAt, qualification: 'estimated', sourceGrade: 'inference' }) - 1;
      return `[Ref ${index + 1}]`;
    }).join(' ');
    return citedSources;
  };
  const developmentPathway = report.developmentPathway.map(row => {
    // Generated rows can omit narrative fields even after a review pass.
    // Withhold projection on malformed rows without inventing identity/prose;
    // retain the original row for the existing quality/schema diagnostics.
    if (!row || typeof row !== 'object' || typeof row.phase !== 'string'
      || typeof row.title !== 'string' || typeof row.description !== 'string') return row;
    const description = row.description.split(MARKER)[0]!;
    const phase = resolveDevelopmentPathwayPhase(row, intent?.phases ?? []);
    const cases = usable && phase ? screen!.additionalScenarios?.filter(item => {
      if (item.scenarioId !== `${phase.id}:it` && item.scenarioId !== `${phase.id}:facility`) return false;
      const sourceScope = data?.waterUseScenarioScreens?.find(water => water.scenarioId === item.scenarioId)?.result.loadRange;
      // A report-only capacity override cannot silently inherit a prior phase
      // calculation just because it retained the same phase identifier.
      if (sourceScope?.phaseBasis !== phase.id || sourceScope.low !== phase.capacityMW
        || sourceScope.high !== phase.capacityMW) return false;
      if (intent?.powerBasis && !isIntentFieldExplicitlyUnknown(intent, 'powerBasis')
        && sourceScope.basis !== (intent.powerBasis === 'it-load' ? 'it' : 'facility')) return false;
      const pue = item.directJobsMwRange?.loadRange.basisConversionPue;
      if (sourceScope.basis === 'it' && intent?.pueValue != null && !isIntentFieldExplicitlyUnknown(intent, 'pueValue')
        && (pue?.low !== intent.pueValue || pue?.high !== intent.pueValue)) return false;
      return true;
    }) ?? [] : [];
    const lines: string[] = [];
    for (const item of cases) {
      if (item.directJobs.siteId !== report.siteId) continue;
      const range = item.directJobsMwRange;
      const basis = item.scenarioId.endsWith(':it') ? 'IT-load interpretation' : 'facility-load interpretation';
      if (range) {
        if (range.siteId !== report.siteId || range.status !== 'calculated'
          || range.lowEndpoint.siteId !== report.siteId || range.highEndpoint.siteId !== report.siteId) continue;
        const low = staffingBand(range.lowEndpoint);
        const high = staffingBand(range.highEndpoint);
        if (!low || !high) continue;
        const pue = range.loadRange.basisConversionPue;
        lines.push(`Modeled ${basis}: ${low} at the lower converted-load endpoint; ${high} at the upper endpoint.${pue ? ` Screening PUE: ${pue.low}–${pue.high} (${pue.source === 'site-record' ? 'site record' : 'screening assumption'}).` : ''}`);
      } else {
        const band = staffingBand(item.directJobs);
        if (!band) continue;
        lines.push(`Modeled ${basis}: ${band}.`);
      }
      lines.push(item.assumption);
    }
    if (!lines.length) return description === row.description ? row : { ...row, description };
    lines.push('Each estimate describes the cumulative phase, not additional hiring; do not add phases or alternative load interpretations. These are benchmark screening estimates, not an operator staffing plan or committed jobs. Workload and operating-model effects are unresolved; facility operations, IT/site operations and contractors cannot be separated defensibly. Construction, offsite, indirect and induced employment are excluded.');
    lines.push(`Methodology and benchmark sources: ${citeSources()}`);
    return { ...row, description: `${description}${MARKER}${lines.join('\n')}` };
  });
  return developmentPathway.some((row, index) => row !== report.developmentPathway![index])
    ? { ...report, developmentPathway, references } : report;
}
